import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import PageShell from "../components/PageShell";
import { ArrowRight, FileCheck, PenTool, ShieldCheck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";

const BASE = "https://a60da7d5-98cd-4edf-8f8b-29323669d79.vercel.app";

const businessCards = [
  { slug: "regular-business-cards", title: "Regular Business Cards", desc: "Full-colour both sides on premium 16PT stock with a matte or gloss finish. The everyday workhorse card.", img: `${BASE}/images/generated/print-business-cards-regular.png`, tiers: [["100", 30], ["250", 36], ["500", 40], ["1,000", 50], ["2,500", 120], ["5,000", 180], ["10,000", 300]], unit: "cards" },
  { slug: "spot-uv-business-cards", title: "Spot UV Business Cards + Velvet", desc: "Soft-touch velvet lamination with high-gloss spot UV raised over your logo. The card people keep turning over.", img: `${BASE}/images/generated/print-business-cards-spot-uv.png`, tiers: [["100", 150], ["250", 240], ["500", 280], ["1,000", 450]], unit: "cards" },
  { slug: "thick-business-cards", title: "Extra Thick Business Cards (32PT)", desc: "Double-weight 32PT stock with a visible chunky edge. Substantial in the hand and hard to bend.", img: `${BASE}/images/generated/print-business-cards-thick.png`, tiers: [["100", 150], ["250", 165], ["500", 190], ["1,000", 220]], unit: "cards" },
  { slug: "raised-foil-business-cards", title: "Raised Foil Business Cards", desc: "Metallic foil raised off the surface in gold, silver, copper or rose. Catches the light from across a room.", img: `${BASE}/images/generated/print-business-cards-raised-foil.png`, tiers: [["100", 195], ["250", 230], ["500", 300]], unit: "cards" },
  { slug: "plastic-business-cards", title: "Plastic Business Cards (20PT)", desc: "Waterproof 20PT plastic with rounded corners — frosted, clear or solid white. Effectively indestructible.", img: `${BASE}/images/generated/print-business-cards-plastic.png`, tiers: [["100", 195], ["250", 230], ["500", 300], ["1,000", 450]], unit: "cards" },
];

const flyer = { slug: "flyers", title: "Flyers (4x6 Inch, 16PT Glossy)", desc: "4×6 inch, 16PT glossy stock, full colour front and back. Ideal for events, promos and door drops.", img: `${BASE}/images/generated/print-flyers.png`, tiers: [["100", 50], ["250", 70], ["500", 85], ["1,000", 100], ["2,500", 200], ["5,000", 290], ["10,000", 520]], unit: "flyers" };

const yardSign = { slug: "yard-signs", title: 'Yard Signs (Color Both Sides)', desc: "Full-colour on both faces, UV-stable ink on 4mm corrugated plastic. Price shown is for one sign.", img: `${BASE}/images/generated/print-yard-signs.png`, tiers: [['12" x 18"', 20], ['18" x 24"', 20], ['24" x 24"', 22], ['24" x 36"', 34], ['48" x 24"', 45], ['48" x 48"', 90], ['96" x 24"', 90], ['96" x 48"', 175]], unit: "size", note: "Priced per sign. H-Sticks sold separately below." };
const hStick = { slug: "h-sticks", title: "H-Sticks", desc: "Galvanised wire H-stick that slides straight into the flutes of a corrugated sign. Priced each.", img: `${BASE}/images/generated/print-yard-signs.png`, tiers: [["Each", 5]], unit: "stick" };

const carMagnet = { slug: "car-magnets", title: "Car Magnets", desc: "Full-colour vehicle door magnets, printed and laminated. Every price below is for a set of two.", img: `${BASE}/images/generated/print-car-magnets.png`, tiers: [['18" x 12"', 65], ['24" x 14"', 95], ['24" x 18"', 110]], unit: "size", note: "Sold as a set of 2." };

const banner = { slug: "retractable-banners", title: 'Retractable Banners (33" x 80")', desc: "Choose your base hardware — all five ship as a complete kit with the printed banner and carry bag. Price is for one banner.", img: `${BASE}/images/generated/print-retractable-banners.png`, tiers: [["Regular", 165], ["Black", 190], ["Premium", 210], ["Deluxe", 210], ["NFC / Black", 210]], unit: "style", note: 'All styles are 33" × 80". Price is per banner.' };

export default function BusinessPrinting() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [location]);
  return (
    <PageShell kicker="Business Printing" title="Print for your business" subtitle="Business cards, flyers, yard signs, car magnets and retractable banners — real prices, no waiting on a quote. Starting at $20.00.">

      {/* Feature strip */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: FileCheck, title: "Print-ready artwork required", desc: "Send us a press-ready PDF, AI or high-res PNG at 300 DPI with a 0.125\" bleed. We proof every file before it goes on press." },
          { icon: PenTool, title: "No artwork yet? We'll make it", desc: "Our design service can build the file for you for an additional fee — layout, logo and all.", cta: { label: "See the design service", to: "/custom-design" } },
          { icon: ShieldCheck, title: "Proof before print, every time", desc: "Nothing goes on press until you've seen and approved a digital proof. Rush turnaround is available on most items." },
        ].map((f, i) => {
          const Icon = f.icon;
          return (
            <div key={i} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6">
              <div className="w-10 h-10 rounded-full bg-[color:var(--dms-peach-light)]/60 flex items-center justify-center"><Icon className="w-4 h-4 text-[color:var(--dms-navy)]" /></div>
              <h3 className="font-serif text-lg text-[color:var(--dms-navy)] mt-4">{f.title}</h3>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">{f.desc}</p>
              {f.cta && <Link to={f.cta.to} className="link-peach mt-3 inline-flex text-[11px]">{f.cta.label} <ArrowRight className="w-3.5 h-3.5" /></Link>}
            </div>
          );
        })}
      </section>

      <SectionGroup id="business-cards" kicker="Business Cards" title="Cards that feel like the real thing" desc="Five stocks, from an everyday matte card to 32PT extra thick and raised metallic foil. Full colour both sides, cut and boxed ready to hand out." items={businessCards} />
      <SectionGroup kicker="Flyers" title="Hand-outs that don't get binned" desc="Heavyweight 16PT glossy card rather than flimsy paper, printed full colour on both sides." items={[flyer]} />
      <SectionGroup kicker="Yard Signs" title="Built for the roadside" desc="Weatherproof corrugated plastic, printed in full colour on both sides. Priced per sign — add H-stakes as you need them." items={[yardSign, hStick]} />
      <SectionGroup kicker="Car Magnets" title="Turn the work van into a billboard" desc="Heavy 30mil magnetic sheet with rounded corners and a UV-protective laminate. Sold as a matched pair." items={[carMagnet]} />
      <SectionGroup kicker="Retractable Banners" title="Set up in under a minute" desc='Standard 33" × 80" roll-up banners. Every option includes the stand, the printed banner and a padded carry bag.' items={[banner]} />

      <section className="bg-[color:var(--dms-navy)] py-16">
        <div className="max-w-3xl mx-auto text-center px-5">
          <p className="kicker">Custom print</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mt-4">Need something not listed?</h2>
          <p className="mt-5 text-white/75">Brochures, stickers, table covers, oversized banners, unusual stocks or a bigger quantity — tell us the item, the quantity and the date you need it by, and we'll come back with a firm number.</p>
          <div className="mt-8"><Link to="/contact" className="btn-peach">Request a custom quote <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>
    </PageShell>
  );
}

function SectionGroup({ id, kicker, title, desc, items }) {
  return (
    <section id={id} className="py-16 border-t border-[color:var(--dms-navy)]/10 scroll-mt-24">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 text-center">
        <p className="kicker">{kicker}</p>
        <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">{title}</h2>
        <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
        <p className="mt-5 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">{desc}</p>
      </div>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((it) => <PrintCard key={it.slug} item={it} />)}
      </div>
    </section>
  );
}

function PrintCard({ item }) {
  const [tier, setTier] = useState(0);
  const { addItem } = useCart();
  const { toast } = useToast();
  const [label, unitPrice] = item.tiers[tier];

  const addToCart = () => {
    addItem({
      slug: item.slug,
      title: `${item.title} — ${label} ${item.unit}`,
      price: `$${unitPrice.toFixed(2)}`,
      img: item.img,
      color: "#faf7f2",
      colorName: label,
      size: label,
      qty: 1,
    });
    toast({ title: "Added to cart", description: `${item.title} · ${label}` });
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/10 flex flex-col">
      <div className="aspect-[4/3] overflow-hidden bg-[color:var(--dms-peach-light)]/30">
        <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="font-serif text-xl text-[color:var(--dms-navy)]">{item.title}</h3>
        <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">{item.desc}</p>

        <div className="mt-5">
          <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{item.tiers.length > 1 ? `${item.unit === "size" || item.unit === "style" ? item.unit.charAt(0).toUpperCase() + item.unit.slice(1) : "Quantity"} — pick one` : "Price"}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {item.tiers.map(([lab, price], i) => (
              <button key={lab} onClick={() => setTier(i)} className={`px-3 py-2 rounded-full border text-xs font-medium transition ${tier === i ? "bg-[color:var(--dms-navy)] text-white border-[color:var(--dms-navy)]" : "bg-white text-[color:var(--dms-navy)] border-[color:var(--dms-navy)]/20 hover:border-[color:var(--dms-navy)]"}`}>
                {lab} <span className={tier === i ? "opacity-70" : "text-[color:var(--dms-navy)]/50"}>· ${price.toFixed(2)}</span>
              </button>
            ))}
          </div>
          {item.note && <p className="mt-3 text-xs text-[color:var(--dms-navy)]/55">{item.note}</p>}
        </div>

        <div className="mt-6 flex items-end justify-between">
          <div>
            <p className="text-[10px] tracking-widest uppercase text-[color:var(--dms-navy)]/55">Total</p>
            <p className="font-serif text-3xl text-[color:var(--dms-navy)]">${unitPrice.toFixed(2)}</p>
            <p className="text-[11px] text-[color:var(--dms-navy)]/55">{label} {item.unit === "size" || item.unit === "style" ? "" : item.unit}</p>
          </div>
          <button onClick={addToCart} className="btn-peach">Add to Cart <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
